/**
 * @typedef {object} SegmentDirection
 * @property {number|string} id
 * @property {number} direction_index 0 - "туда"
 * @property {bool} is_direct с пересадкой или без
 * @property {string} departure_airport_iata аэропорт вылета
 * @property {string} arrival_airport_iata аэропорт прибытия
 * @property {string} departure_datetime
 * @property {string} arrival_datetime
 * @property {string} flight_number
 * @property {string} airline_code
 */

/**
 * @typedef {Object} Passenger
 * @property {number} id
 * @property {number} age
 * @property {string} sex
 * @property {string} category_code
 */

/**
 * @typedef {Object} SegmentChangesItem данные изменения в сегменте при добавлении, удалении, изменении количества услуг
 * @property {String} currency ISO3 код валюты
 * @property {String} item_id
 * @property {Number} item_price
 * @property {Number} item_quantity новое количество
 * @property {Number} item_quantity_diff изменение количества, может быть отрицательном при уменьшении
 * @property {Number} passenger_id
 * @property {Number} segment_id
 */

/**
 * @typedef {Object} Price
 * @property {string|int} airline_svc_id
 * @property {string} airline_svc_type
 * @property {string|int} order_passenger_id
 * @property {string|int} order_segment_id
 * @property {string|int|null} order_svc_id
 * @property {string} code категория пассажира
 * @property {string} validating_company
 * @property {string} currency
 * @property {string|int|float} total
 * @property {string|int} airline_svc_sort
 */

/**
 * @typedef {Object} Svc
 * @property {int|string} id
 * @property {string} airline_code
 * @property {string|int|null} instance_id
 * @property {string} code
 * @property {string} type
 * @property {string|int} category_id
 * @property {string} rfic
 * @property {string} rfisc
 * @property {string} service_type
 * @property {string} emd
 * @property {string|null} ssr
 * @property {string} name
 * @property {string} name_ru
 * @property {string|int} max_quantity
 * @property {string} description
 * @property {string} description_ru
 * @property {string|null} hint
 * @property {string|null} hint_ru
 * @property {string|null} img_path
 * @property {string|int|boolean|null} allow_zero_price
 * @property {string|int|null} sort
 * @property {string|int|boolean|null} deleted
 * @property {string|null} create_datetime
 * @property {string|null} update_datetime
 * @property {object|null} airline
 * @property {object|null} category
 *
 */

var AnalyticsSvcsDataset = (function ($) {

    AnalyticsSvcsDataset = function (data) {
        /**
         * @type {SegmentDirection[]}
         */
        this.segment_directions = data.segment_directions;

        /** @type {bool} */
        this.is_oneway = data.is_oneway;

        /** @type {string} флоу, основной или докупка (main|add) */
        this.layout = data.layout;

        /** @type {number|string} код витрины */
        this.storefront = data.storefront;

        /** @type {Passenger[]} */
        this.passengers = data.passengers;

        this.directions = data.directions || [];

        this.svcs = data.svcs || {};

        this.selectors = {
            select_segment_passengers: '.js-select-segment-passengers',
            select_passenger: '.js-select-passenger',
            select_passenger_action_select: '.js-select-passenger-action-select'
        }

        this.CONNECTED_FLIGHT = 'Connected';
        this.DIRECT_FLIGHT = 'Direct';

        this.TYPE_EMD = 'EMD';
        this.TYPE_ETM = 'ETM';

        // используется для генерации view_item_list всех вариантов багажа
        this.LUGGAGE_TYPES = ['LUGGAGE', 'HAND_LUGGAGE', 'HAND_LUGGAGE_UP'];
    }

    /**
     * @param {number|string} segment_id
     * @return {SegmentDirection|null}
     */
    AnalyticsSvcsDataset.prototype.getSegmentDirectionBySegmentId = function (segment_id) {
        segment_id = parseInt(segment_id, 10);
        return this.segment_directions.find(sd => sd.segment_id === segment_id);
    }

    /**
     * Возвращает цены для всех пассажиро-сегментов одной услуги. Пригодится для списков багажа
     *
     * @param {Svc|string|number} svc
     * @param {string|int} [segment_id]
     */
    AnalyticsSvcsDataset.prototype.getSvcViewItemListBySvcId = function (svc, segment_id) {
        var that = this;

        if (typeof svc !== 'object') {
            svc = that._getSvcById(svc);
        }

        if (segment_id) {
            segment_id = parseInt(segment_id, 10);
        }

        return that._getPricesForSvc(svc)
                   .map(price_item => {
                       var segment = that.getSegmentDirectionBySegmentId(price_item.order_segment_id),
                           passenger = that._getPassengerById(price_item.order_passenger_id);

                       if (!segment || !passenger) {
                           return null;
                       }
                       if (segment_id && segment_id !== parseInt(segment.segment_id, 10)) {
                           return null;
                       }

                       return that._buildItem(svc, price_item, segment, passenger);
                   })
                   .filter(item => item !== null);
    }

    /**
     *
     * @param {Svc} svc
     * @param {Price} price_item
     * @param {SegmentDirection} segment
     * @param {Passenger} passenger
     * @private
     */
    AnalyticsSvcsDataset.prototype._buildItem = function (svc, price_item, segment, passenger) {
        const that = this;
        return {
            item_variant: that.layout,
            item_name: svc.code,
            item_id: [
                that.TYPE_EMD,
                svc.rfisc,
                segment.airline_code,
                segment.flight_number,
                segment.departure_airport_iata,
                segment.arrival_airport_iata,
                segment.departure_datetime.substring(0, 10).replaceAll('-', ''),
                segment.direction_index,
                segment.is_direct ? that.DIRECT_FLIGHT : that.CONNECTED_FLIGHT,
                passenger.category_code
            ].join('_'),
            item_brand: svc.rfisc,
            affiliation: that.storefront,
            price: that._round(price_item.total, 2),
            currency: price_item.currency,
            quantity: 1,
            item_category: that.TYPE_EMD,
            item_category2: segment.departure_airport_iata,
            item_category3: segment.arrival_airport_iata,
            item_category4: segment.departure_datetime.substring(0, 10,),
            item_category5: segment.arrival_datetime.substring(0, 10,),
            item_category6: segment.direction_index,
            item_category7: (segment.is_direct ? that.DIRECT_FLIGHT : that.CONNECTED_FLIGHT),
            item_category8: (segment.is_direct ? that.DIRECT_FLIGHT : that.CONNECTED_FLIGHT),
            item_category9: segment.flight_number,
            item_category10: passenger.category_code,
            item_category11: passenger.sex,
            item_category12: passenger.age
        };
    }

    /**
     * @param {string|number|Svc} svc id услуги или объект из svcs.svcs
     * @return {Price[]}
     * @private
     */
    AnalyticsSvcsDataset.prototype._getPricesForSvc = function (svc) {
        var that = this;

        if (typeof svc !== 'object') {
            svc = that._getSvcById(svc);
        }

        if (!svc) {
            return [];
        }

        var svc_id = parseInt(svc.id, 10);

        if (svc_id && that.svcs.prices && that.svcs.prices[svc.type] && Array.isArray(that.svcs.prices[svc.type])) {
            return that.svcs.prices[svc.type].filter(price_item => svc_id === parseInt(price_item.airline_svc_id, 10));
        }

        return [];
    }

    /**
     * @param {int|string|Svc} svc_id
     * @param {string} [svc_type]
     * @return {{item_list_name: string, items}|null}
     */
    AnalyticsSvcsDataset.prototype.getViewItemListForSingleSvc = function (svc_id, svc_type) {
        var that = this;

        if (!svc_id) {
            return null;
        }

        var svc = that._getSvcById(svc_id);

        if (!svc) {
            return null;
        }

        var items = that.getSvcViewItemListBySvcId(svc);

        if (!svc_type) {
            svc_type = that._capitalize(svc.type);
        }

        return items && items.length ? {
            item_list_name: `EMD ${svc_type} ${svc.code}`,
            items: items
        } : null;
    }

    /**
     * view_item_list всех пассажиро-сегментов всех услуг указанного типа
     *
     * @param {string} type
     * @param {string|int} [segment_id]
     * @param {string|int} [category_id]
     */
    AnalyticsSvcsDataset.prototype.getViewItemListForType = function (type, segment_id, category_id) {
        var that = this,
            items = [],
            item,
            svc,
            category_name,
            all_segment_id;

        type = type.toUpperCase();

        if (segment_id) {
            all_segment_id = [parseInt(segment_id, 10)];
        } else {
            all_segment_id = that.segment_directions.map(sd => sd.segment_id);
        }
        category_id = category_id && category_id !== 'all' ? parseInt(category_id, 10) : 0;

        for (var svc_id in that.svcs.svcs) {
            svc = that.svcs.svcs[svc_id];
            if (svc.type === type) {
                if (category_id && parseInt(svc.category_id, 10) !== category_id) {
                    continue;
                }
                if (category_id && !category_name) {
                    category_name = svc.category.name;
                }
                all_segment_id.forEach(si => {
                    item = that.getSvcViewItemListBySvcId(parseInt(svc.id, 10), si || 0);
                    if (item.length) {
                        items = items.concat(item);
                    }
                });
            }
        }

        if (!items.length) {
            return null;
        }

        type = that._capitalize(type);

        return {
            item_list_name: `EMD ${type}` + (category_name ? ` (${category_name})` : ''),
            items: items
        }
    }

    /**
     * @param {string|number} id
     * @return {Svc|null}
     * @private
     */
    AnalyticsSvcsDataset.prototype._getSvcById = function (id) {
        var that = this;
        if (that.svcs && that.svcs.svcs && that.svcs.svcs[id]) {
            return that.svcs.svcs[id];
        }

        return null;
    };

    /**
     *
     * @param {number|string} id
     * @return {Passenger|undefined}
     * @private
     */
    AnalyticsSvcsDataset.prototype._getPassengerById = function (id) {
        var that = this;
        return that.passengers.find(passenger => parseInt(passenger.id, 10) === parseInt(id, 10));
    }

    /**
     *
     * @param {SegmentChangesItem|SegmentChangesItem[]} data
     * @param {UISelectSegmentPassengers} [component]
     * @return {object}
     */
    AnalyticsSvcsDataset.prototype.svcCartChange = function (data, component) {
        var that = this, currency = that.svcs.currency || null;

        if (component && component.currency) {
            currency = component.currency;
        }

        var mapItemCallback = data_item => {
            var segment_data = that.getSegmentDirectionBySegmentId(data_item.segment_id),
                svc = that._getSvcById(data_item.item_id),
                passenger = that._getPassengerById(data_item.passenger_id);
            if (!svc || !passenger) {
                return null;
            }
            if (!currency && data_item.currency) {
                currency = data_item.currency;
            }
            return {
                item_variant: that.layout,
                item_name: svc.code,
                item_id: [
                    'EMD',
                    svc.rfisc,
                    segment_data.airline_code,
                    segment_data.flight_number,
                    segment_data.departure_airport_iata,
                    segment_data.arrival_airport_iata,
                    segment_data.departure_datetime.substring(0, 10).replaceAll('-', ''),
                    segment_data.direction_index,
                    segment_data.is_direct ? that.DIRECT_FLIGHT : that.CONNECTED_FLIGHT,
                    passenger.category_code
                ].join('_'),
                item_brand: svc.rfisc,
                affiliation: this.storefront,
                price: this._round(data_item.item_price, 2),
                currency: data_item.currency,
                coupon: '', //
                quantity: Math.abs(data_item.item_quantity_diff),
                item_category: 'EMD',
                item_category2: segment_data.departure_airport_iata,
                item_category3: segment_data.arrival_airport_iata,
                item_category4: segment_data.departure_datetime.substring(0, 10),
                item_category5: segment_data.arrival_datetime.substring(0, 10),
                item_category6: (segment_data.is_direct ? that.DIRECT_FLIGHT : that.CONNECTED_FLIGHT),
                item_category7: '' + segment_data.direction_index,
                item_category8: segment_data.airline_code,
                item_category9: segment_data.flight_number,
                item_category10: passenger.category_code,
                item_category11: passenger.sex,
                item_category12: passenger.age
            }
        };

        var added_items = (Array.isArray(data) ? data : [data])
                .filter(data_item => data_item.item_quantity_diff > 0)
                .map(mapItemCallback)
                .filter(data_item => !!data_item),

            removed_items = (Array.isArray(data) ? data : [data])
                .filter(data_item => data_item.item_quantity_diff < 0)
                .map(mapItemCallback)
                .filter(data_item => !!data_item);

        return {
            add_to_cart: added_items.length
                ? {
                    currency: currency,
                    value: that._round(added_items.reduce((acc, v) => acc + v.quantity * v.price, 0), 2),
                    items: added_items
                }
                : null,
            remove_from_cart: removed_items.length
                ? {
                    currency: currency,
                    value: that._round(removed_items.reduce((acc, v) => acc + v.quantity * v.price, 0), 2),
                    items: removed_items
                }
                : null
        }
    }

    /**
     * @param {number|string} num
     * @param {number|string} precision
     * @return {number}
     * @private
     */
    AnalyticsSvcsDataset.prototype._round = function (num, precision) {
        precision = parseInt(precision || 0, 10);
        var multiplier = Math.pow(10, precision);

        return Math.round(parseFloat(num) * multiplier) / multiplier;
    }

    /**
     * @param {string} str
     * @return {string}
     * @private
     */
    AnalyticsSvcsDataset.prototype._capitalize = function (str) {
        if (str.length) {
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        }

        return str;
    }

    return AnalyticsSvcsDataset;
})(jQuery)
